import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { DataItem, FolderItem, FolderDataLink, WidgetItem, HistoryItem, TagItem, WidgetPage } from '@/types';

export const DB_NAME = 'my-favorite-copies';
export const DB_VERSION = 2;
export const DEFAULT_WIDGET_PAGE_ID = 'default-widget-page';
export const DEFAULT_WIDGET_PAGE_COLUMNS = 4;
export const DEFAULT_WIDGET_PAGE_ROWS = 3;

export function createDefaultWidgetPage(now = Date.now()): WidgetPage {
  return {
    id: DEFAULT_WIDGET_PAGE_ID,
    name: '페이지 1',
    order: 0,
    columns: DEFAULT_WIDGET_PAGE_COLUMNS,
    rows: DEFAULT_WIDGET_PAGE_ROWS,
    createdAt: now,
    updatedAt: now,
  };
}

export function normalizeWidgetPageId(widget: WidgetItem): WidgetItem {
  return {
    ...widget,
    pageId: widget.pageId || DEFAULT_WIDGET_PAGE_ID,
  };
}

export interface AppDBSchema extends DBSchema {
  data: { key: string; value: DataItem; indexes: { 'by-type': string; 'by-created': number } };
  folders: { key: string; value: FolderItem; indexes: { 'by-parent': string } };
  folderLinks: { key: string; value: FolderDataLink; indexes: { 'by-folder': string; 'by-data': string } };
  widgets: { key: string; value: WidgetItem; indexes: { 'by-data': string; 'by-order': number; 'by-page-order': [string, number] } };
  widgetPages: { key: string; value: WidgetPage; indexes: { 'by-order': number } };
  history: { key: string; value: HistoryItem; indexes: { 'by-created': number } };
  tags: { key: string; value: TagItem; indexes: { 'by-name': string } };
  settings: { key: string; value: { key: string; value: string } };
}

export type AppDB = IDBPDatabase<AppDBSchema>;

let dbInstance: AppDB | null = null;

function createV1Stores(db: AppDB) {
  if (!db.objectStoreNames.contains('data')) {
    const dataStore = db.createObjectStore('data', { keyPath: 'id' });
    dataStore.createIndex('by-type', 'type');
    dataStore.createIndex('by-created', 'createdAt');
  }

  if (!db.objectStoreNames.contains('folders')) {
    const folderStore = db.createObjectStore('folders', { keyPath: 'id' });
    folderStore.createIndex('by-parent', 'parentId');
  }

  if (!db.objectStoreNames.contains('folderLinks')) {
    const linkStore = db.createObjectStore('folderLinks', { keyPath: 'id' });
    linkStore.createIndex('by-folder', 'folderId');
    linkStore.createIndex('by-data', 'dataId');
  }

  if (!db.objectStoreNames.contains('widgets')) {
    const widgetStore = db.createObjectStore('widgets', { keyPath: 'id' });
    widgetStore.createIndex('by-data', 'dataId');
    widgetStore.createIndex('by-order', 'order');
  }

  if (!db.objectStoreNames.contains('history')) {
    const historyStore = db.createObjectStore('history', { keyPath: 'id' });
    historyStore.createIndex('by-created', 'createdAt');
  }

  if (!db.objectStoreNames.contains('tags')) {
    const tagStore = db.createObjectStore('tags', { keyPath: 'id' });
    tagStore.createIndex('by-name', 'name');
  }

  if (!db.objectStoreNames.contains('settings')) {
    db.createObjectStore('settings', { keyPath: 'key' });
  }
}

function createWidgetPagesStore(db: AppDB) {
  if (db.objectStoreNames.contains('widgetPages')) return;

  const pageStore = db.createObjectStore('widgetPages', { keyPath: 'id' });
  pageStore.createIndex('by-order', 'order');
}

function migrateWidgetsToDefaultPage(transaction: Parameters<NonNullable<Parameters<typeof openDB<AppDBSchema>>[2]>['upgrade']>[3]) {
  const widgetStore = transaction.objectStore('widgets');

  if (!widgetStore.indexNames.contains('by-page-order')) {
    widgetStore.createIndex('by-page-order', ['pageId', 'order']);
  }

  transaction.objectStore('widgetPages').put(createDefaultWidgetPage());

  void widgetStore.openCursor().then(function updateCursor(cursor): Promise<void> | void {
    if (!cursor) return;

    const widget = normalizeWidgetPageId(cursor.value);
    const update = cursor.value.pageId ? Promise.resolve() : cursor.update(widget);
    return update.then(() => cursor.continue()).then(updateCursor);
  });
}

export async function getDB(): Promise<AppDB> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<AppDBSchema>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) {
        createV1Stores(db);
      }

      if (oldVersion < 2) {
        createWidgetPagesStore(db);
        migrateWidgetsToDefaultPage(transaction);
      }
    },
  }) as unknown as AppDB;

  return dbInstance;
}
